import React, { useCallback, useEffect, useState } from "react";
import { AuthProvider, useAuth } from "./AuthContext";
import { DebugProvider } from "./DebugContext";
import Login from "./Login";
import FinanceDashboard from "./FinanceDashboard";
import CreditUnionDashboard from "./CreditUnionDashboard";
import AdminDashboard from "./AdminDashboard";
import {
  readLogicalPathname,
  withPublicBase,
  normalizeLogicalPath,
} from "./appPaths";

const ADMIN_PERMISSION_BY_TAB = {
  users: "admin.users",
  roles: "admin.roles",
  permissions: "admin.permissions",
};
const ADMIN_TABS = Object.keys(ADMIN_PERMISSION_BY_TAB);

function parseRoute(logicalPathname) {
  const path = normalizeLogicalPath(logicalPathname);
  if (path === "/" || path === "/finance") return { section: "finance", adminTab: "users" };
  if (path === "/cu" || path === "/cu-reports") return { section: "cu", adminTab: "users" };
  if (path.startsWith("/admin")) {
    const parts = path.split("/");
    const requestedTab = parts[2];
    const adminTab = ADMIN_TABS.includes(requestedTab) ? requestedTab : "users";
    return { section: "admin", adminTab };
  }
  return { section: "finance", adminTab: "users" };
}

function buildAdminPath(tab) {
  return `/admin/${tab}`;
}

function AppShell() {
  const { user, loading, hasPermission } = useAuth();
  const [routeState, setRouteState] = useState(() => parseRoute(readLogicalPathname()));

  const navigate = useCallback((logicalPath, replace = false) => {
    const normalizedLogical = normalizeLogicalPath(
      logicalPath.startsWith("/") ? logicalPath : `/${logicalPath}`,
    );
    const currentLogical = readLogicalPathname();
    const fullPath = withPublicBase(normalizedLogical);
    if (normalizedLogical !== currentLogical) {
      const writeHistory = replace ? window.history.replaceState : window.history.pushState;
      writeHistory.call(window.history, {}, "", fullPath);
    }
    setRouteState(parseRoute(normalizedLogical));
  }, []);

  useEffect(() => {
    const onPopState = () => {
      setRouteState(parseRoute(readLogicalPathname()));
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    const canCu = hasPermission("cu.reports.view");
    const canAnyAdmin = ADMIN_TABS.some((tab) => hasPermission(ADMIN_PERMISSION_BY_TAB[tab]));

    if (routeState.section === "cu" && !canCu) {
      navigate("/finance", true);
      return;
    }

    if (routeState.section === "admin") {
      if (!canAnyAdmin) {
        navigate("/finance", true);
        return;
      }
      const requestedTab = routeState.adminTab || "users";
      const tabPermission = ADMIN_PERMISSION_BY_TAB[requestedTab];
      if (!tabPermission || !hasPermission(tabPermission)) {
        const fallbackTab = ADMIN_TABS.find((tab) => hasPermission(ADMIN_PERMISSION_BY_TAB[tab]));
        if (fallbackTab) {
          navigate(buildAdminPath(fallbackTab), true);
        } else {
          navigate("/finance", true);
        }
      }
    }
  }, [routeState, hasPermission, navigate]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "system-ui, sans-serif",
          color: "#64748b",
        }}
      >
        Loading…
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  if (routeState.section === "cu" && hasPermission("cu.reports.view")) {
    return <CreditUnionDashboard onBack={() => navigate("/finance")} />;
  }

  const firstAdminTab = ADMIN_TABS.find((tab) => hasPermission(ADMIN_PERMISSION_BY_TAB[tab])) || "users";
  const canAnyAdmin = ADMIN_TABS.some((tab) => hasPermission(ADMIN_PERMISSION_BY_TAB[tab]));
  if (routeState.section === "admin" && canAnyAdmin) {
    return (
      <AdminDashboard
        onBack={() => navigate("/finance")}
        tab={routeState.adminTab}
        onTabChange={(tab) => navigate(buildAdminPath(tab))}
      />
    );
  }

  return (
    <FinanceDashboard
      onOpenCuReports={
        hasPermission("cu.reports.view") ? () => navigate("/cu-reports") : undefined
      }
      cuReportsHref={withPublicBase("/cu-reports")}
      onOpenAdmin={
        canAnyAdmin
          ? () => navigate(buildAdminPath(firstAdminTab))
          : undefined
      }
      adminHref={canAnyAdmin ? withPublicBase(buildAdminPath(firstAdminTab)) : undefined}
    />
  );
}

export default function App() {
  return (
    <AuthProvider>
      <DebugProvider>
        <AppShell />
      </DebugProvider>
    </AuthProvider>
  );
}
